import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';
import { getTeamMemberBySlug } from '../data/teamData';
import { getServiceBySlug } from '../data/servicesData';

const translations = {
  en: {
    notFoundTitle: 'Team Member Not Found',
    notFoundDesc: 'The requested medical practitioner profile does not exist or has been relocated.',
    backToAbout: 'Back to Medical Team',
    breadcrumbHome: 'Home',
    breadcrumbAbout: 'About Us',
    breadcrumbTeam: 'Medical Team',
    licenseBadge: 'Licensed Practitioner',
    bookConsultation: 'Book Consultation with',
    viewCertificates: 'View Official Certificates',
    verifiedPractitioner: 'Verified Medical Practitioner',
    yearsExpLabel: 'Clinical Experience',
    clientsLabel: 'Clients Treated',
    credentialsTitle: 'Medical Credentials & Authorized Certifications',
    credentialsSub: 'Official Manufacturer Certifications',
    clickToEnlarge: 'Click to view high-resolution certificate',
    closeModal: 'Close',
    issuerLabel: 'Issuing Authority',
    bioTitle: 'Clinical Background & Expertise',
    philosophyTitle: 'Aesthetic Philosophy',
    specialtiesTitle: 'Specialized Treatments & Procedures',
    specialtiesSub: 'Expertly administered treatments aligned with clinical anatomical precision',
    learnMore: 'Explore Treatment',
    ctaHeading: 'Ready for a Personalized Medical Consultation?',
    ctaSub: 'Schedule a one-on-one session to receive a custom anatomical assessment and tailored treatment plan.',
    ctaButton: 'Book Your Consultation Now',
    noCertsFound: 'Certificate documents on file with clinic administration.'
  },
  cn: {
    notFoundTitle: '未找到团队成员档案',
    notFoundDesc: '您查询的医疗专家主页不存在或已被移至其他位置。',
    backToAbout: '返回医疗团队',
    breadcrumbHome: '首页',
    breadcrumbAbout: '关于我们',
    breadcrumbTeam: '医疗团队',
    licenseBadge: '持牌执业医疗人员',
    bookConsultation: '预约面诊咨询：',
    viewCertificates: '查看官方权威证书',
    verifiedPractitioner: '权威认证执业专家',
    yearsExpLabel: '临床执业经验',
    clientsLabel: '服务客户案例',
    credentialsTitle: '执业资质与权威认证展示',
    credentialsSub: '全球厂商官方授权证书背书',
    clickToEnlarge: '点击放大查看高清原图证书',
    closeModal: '关闭预览',
    issuerLabel: '颁发机构',
    bioTitle: '临床背景与专业资历',
    philosophyTitle: '执业与自然美学理念',
    specialtiesTitle: '擅长疗程与特色项目',
    specialtiesSub: '结合解剖学精准度与高转化率的个性化医疗美学方案',
    learnMore: '查看疗程详情',
    ctaHeading: '准备好开启您的定制美学方案了吗？',
    ctaSub: '立即预约一对一面诊，获取专业面部解剖评估与定制化治疗计划。',
    ctaButton: '立即预约专业面诊',
    noCertsFound: '官方认证文件已存档于诊所行政部门。'
  }
};

const TeamMemberDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = translations[lang] || translations.en;

  const member = getTeamMemberBySlug(id, lang);
  const [activeCert, setActiveCert] = useState(null);

  // Force scroll to top when switching team members
  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [id]);

  // Close modal on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveCert(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Redirect protection: if member doesn't exist or profile detail page is disabled, redirect to /about
  useEffect(() => {
    if (!member || !member.hasDetailPage) {
      navigate('/about', { replace: true });
    }
  }, [member, navigate]);

  if (!member || !member.hasDetailPage) {
    return null;
  }

  // Retrieve matching specialty service data objects
  const specialtyServices = (member.specialtySlugs || []).map((slug) => {
    const s = getServiceBySlug(slug);
    if (!s) return null;
    const title = s[lang]?.title || s.en?.title || slug;
    const subtitle = s[lang]?.subtitle || s.en?.subtitle || '';
    return {
      slug: s.slug,
      title,
      subtitle,
      image: s.image
    };
  }).filter(Boolean);

  const canonicalUrl = `https://www.haloaestheticclinic.com/team/${member.slug}`;

  // Structured Data (Schema.org / Person)
  const jsonLdSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    'name': member.name,
    'jobTitle': member.title,
    'description': member.bio,
    'worksFor': {
      '@type': 'MedicalClinic',
      'name': 'Halo Aesthetic Clinic',
      'url': 'https://www.haloaestheticclinic.com'
    },
    'image': member.avatar ? `https://www.haloaestheticclinic.com${member.avatar}` : undefined,
    'url': canonicalUrl,
    'knowsAbout': member.specialties || [],
    'hasCredential': (member.certificates || []).map(c => ({
      '@type': 'EducationalOccupationalCredential',
      'name': c.title,
      'credentialCategory': c.category || 'Certification',
      'recognizedBy': {
        '@type': 'Organization',
        'name': c.issuer
      }
    }))
  };

  return (
    <div className="bg-softGradient bg-no-repeat bg-top text-text min-h-screen">
      <Helmet>
        <title>{`${member.name} | ${member.title} | Halo Aesthetic Clinic Richmond Hill`}</title>
        <meta name="description" content={`${member.name} - ${member.title} at Halo Aesthetic Clinic. ${member.bio.slice(0, 150)}...`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${member.name} | ${member.title} | Halo Aesthetic Clinic`} />
        <meta property="og:description" content={member.bio.slice(0, 160)} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <script type="application/ld+json">{JSON.stringify(jsonLdSchema)}</script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2 text-sm text-gray-500">
        <div className="flex items-center space-x-2">
          <Link to="/" className="hover:text-[#2E6B4C] transition-colors">{t.breadcrumbHome}</Link>
          <span>/</span>
          <Link to="/about" className="hover:text-[#2E6B4C] transition-colors">{t.breadcrumbAbout}</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">{member.name}</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 md:p-10 shadow-card border border-champagne/40 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          
          {/* Avatar & Badges column */}
          <div className="lg:col-span-5 flex flex-col items-center">
            <div className="w-full max-w-sm aspect-[3/4] bg-gray-100 rounded-2xl overflow-hidden relative border-2 border-champagne/50 shadow-lg group">
              <img
                src={member.avatar}
                alt={member.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              
              {/* Overlay Badges */}
              {member.verifiedBadge && (
                <div className="absolute top-4 left-4 bg-[#2E6B4C]/90 backdrop-blur-md text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-md flex items-center space-x-1.5">
                  <svg className="w-4 h-4 text-emerald-300 fill-current" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{member.verifiedBadge}</span>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-3 w-full max-w-sm mt-5">
              <div className="bg-[#2E6B4C]/5 border border-[#2E6B4C]/15 rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-[#2E6B4C]">{member.experienceYears || '8+'}</div>
                <div className="text-[11px] text-gray-600 font-medium leading-tight">{t.yearsExpLabel}</div>
              </div>
              <div className="bg-[#2E6B4C]/5 border border-[#2E6B4C]/15 rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-[#2E6B4C]">{member.clientsTreated || '5000+'}</div>
                <div className="text-[11px] text-gray-600 font-medium leading-tight">{t.clientsLabel}</div>
              </div>
            </div>
          </div>

          {/* Details & Info column */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#2E6B4C]/10 text-[#2E6B4C] border border-[#2E6B4C]/20 mb-3">
                {t.verifiedPractitioner}
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
                {member.name}
              </h1>
              <p className="text-lg md:text-xl text-[#2E6B4C] font-semibold mt-1">
                {member.title}
              </p>
              <p className="text-sm text-gray-500 font-medium mt-0.5">
                {member.role}
              </p>
            </div>

            {/* Key Credentials Badges */}
            {member.keyCredentials && member.keyCredentials.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Key Qualifications</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {member.keyCredentials.map((cred, idx) => (
                    <div key={idx} className="flex items-start space-x-2 text-xs font-medium text-gray-700 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                      <svg className="w-4 h-4 text-[#2E6B4C] shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span>{cred}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Link
                to="/book-form"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-[#2E6B4C] text-white font-semibold rounded-xl hover:bg-[#23533a] transition-all shadow-md hover:shadow-lg text-center"
              >
                <span>{t.bookConsultation} {member.name}</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
              
              {member.certificates && member.certificates.length > 0 && (
                <a
                  href="#credentials"
                  className="inline-flex items-center justify-center px-6 py-3.5 bg-white text-[#2E6B4C] font-semibold rounded-xl border border-[#2E6B4C]/30 hover:bg-[#2E6B4C]/5 transition-all text-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>{t.viewCertificates}</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Credentials & Certificate Showcase Gallery */}
      <section id="credentials" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="text-xs font-bold text-[#2E6B4C] bg-[#2E6B4C]/10 px-3 py-1 rounded-full uppercase tracking-wider">
            Verified Credentials
          </span>
          <h2 className="text-3xl font-extrabold text-gray-900 mt-2 mb-3">
            {t.credentialsTitle}
          </h2>
          <p className="text-gray-600 text-sm md:text-base">
            {t.credentialsSub}
          </p>
        </div>

        {member.certificates && member.certificates.length > 0 ? (
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${member.certificates.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-6`}>
            {member.certificates.map((cert) => (
              <div
                key={cert.id}
                onClick={() => setActiveCert(cert)}
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-xl border border-champagne/40 transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between"
              >
                {/* Image Box */}
                <div className="relative aspect-[4/3] bg-gray-50 overflow-hidden flex items-center justify-center p-3 border-b border-gray-100">
                  <img
                    src={cert.image}
                    alt={cert.title}
                    className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-[#2E6B4C]/60 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center text-white p-4 text-center">
                    <svg className="w-8 h-8 mb-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                    <span className="text-xs font-medium">{t.clickToEnlarge}</span>
                  </div>
                </div>

                {/* Meta details */}
                <div className="p-4 bg-white flex-1 flex flex-col justify-between">
                  <div>
                    <div className="text-[11px] font-semibold text-[#2E6B4C] uppercase tracking-wide mb-1">
                      {cert.issuer}
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 group-hover:text-[#2E6B4C] transition-colors leading-snug">
                      {cert.title}
                    </h3>
                  </div>

                  <div className="mt-4 pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500 font-medium">
                    <span className="inline-flex items-center text-[#2E6B4C]">
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Official Certificate
                    </span>
                    <span className="text-gray-400 group-hover:translate-x-0.5 transition-transform">→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 bg-white/60 rounded-xl border border-dashed border-gray-300 text-gray-500 text-sm">
            {t.noCertsFound}
          </div>
        )}
      </section>

      {/* Lightbox / Modal Modal Pop-up */}
      {activeCert && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/80 backdrop-blur-md p-4 sm:p-6 overflow-y-auto animate-fadeIn"
          onClick={() => setActiveCert(null)}
        >
          <div
            className="relative bg-white rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl border border-white/20 my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 sm:p-6 bg-gray-900 text-white flex items-center justify-between border-b border-gray-800">
              <div>
                <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider block">
                  {t.issuerLabel}: {activeCert.issuer}
                </span>
                <h3 className="text-lg sm:text-xl font-bold text-white mt-0.5">
                  {activeCert.title}
                </h3>
              </div>
              <button
                onClick={() => setActiveCert(null)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-lg font-bold"
                aria-label={t.closeModal}
              >
                ✕
              </button>
            </div>

            {/* Modal Body - High Res Image */}
            <div className="p-4 sm:p-8 bg-neutral-900 flex items-center justify-center min-h-[400px] max-h-[75vh] overflow-auto">
              <img
                src={activeCert.image}
                alt={activeCert.title}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <span>Halo Aesthetic Clinic Verified Medical Record</span>
              <button
                onClick={() => setActiveCert(null)}
                className="px-4 py-2 bg-[#2E6B4C] text-white font-medium rounded-lg hover:bg-[#23533a] transition-colors"
              >
                {t.closeModal}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bio & Philosophy Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Bio card */}
          <div className="lg:col-span-7 bg-white/80 backdrop-blur-md rounded-2xl p-6 md:p-8 shadow-card border border-champagne/40 space-y-4">
            <div className="flex items-center space-x-2 text-[#2E6B4C]">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h2 className="text-xl font-bold text-gray-900">{t.bioTitle}</h2>
            </div>
            <p className="text-gray-700 leading-relaxed text-base whitespace-pre-line">
              {member.bio}
            </p>
          </div>

          {/* Philosophy Card */}
          <div className="lg:col-span-5 bg-gradient-to-br from-[#2E6B4C] to-[#1e4833] text-white rounded-2xl p-6 md:p-8 shadow-xl flex flex-col justify-between space-y-6">
            <div>
              <div className="flex items-center space-x-2 text-emerald-300 mb-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <h3 className="text-lg font-bold text-white uppercase tracking-wider">{t.philosophyTitle}</h3>
              </div>
              <blockquote className="text-emerald-50 italic text-lg leading-relaxed font-serif relative">
                “{member.philosophy || member.bio.slice(0, 180)}”
              </blockquote>
            </div>

            <div className="pt-4 border-t border-emerald-600/50 flex items-center justify-between text-xs text-emerald-200">
              <span className="font-semibold">— {member.name}, {member.title}</span>
              <span className="bg-emerald-800/60 px-2.5 py-1 rounded-md">Halo Aesthetic Clinic</span>
            </div>
          </div>
        </div>
      </section>

      {/* Specialties Matrix Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="text-xs font-bold text-[#2E6B4C] bg-[#2E6B4C]/10 px-3 py-1 rounded-full uppercase tracking-wider">
            Clinical Focus
          </span>
          <h2 className="text-3xl font-extrabold text-gray-900 mt-2 mb-3">
            {t.specialtiesTitle}
          </h2>
          <p className="text-gray-600 text-sm md:text-base">
            {t.specialtiesSub}
          </p>
        </div>

        {/* Linked Services Cards Grid */}
        {specialtyServices.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialtyServices.map((svc) => (
              <div
                key={svc.slug}
                className="bg-white rounded-2xl overflow-hidden shadow-card border border-champagne/40 flex flex-col justify-between hover:shadow-xl transition-all duration-300 group"
              >
                <div>
                  <div className="aspect-[16/9] bg-gray-100 overflow-hidden relative">
                    <img
                      src={svc.image}
                      alt={svc.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent opacity-60" />
                    <div className="absolute bottom-3 left-4 right-4 text-white">
                      <h3 className="text-lg font-bold drop-shadow-md">{svc.title}</h3>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-gray-600 text-xs md:text-sm line-clamp-3 mb-4 leading-relaxed">
                      {svc.subtitle}
                    </p>
                  </div>
                </div>

                <div className="px-5 pb-5 pt-0">
                  <Link
                    to={`/services/${svc.slug}`}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-[#2E6B4C]/10 text-[#2E6B4C] font-semibold text-xs rounded-xl hover:bg-[#2E6B4C] hover:text-white transition-all duration-300"
                  >
                    <span>{t.learnMore}</span>
                    <svg className="w-3.5 h-3.5 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Fallback Pills list if no slug services */
          <div className="flex flex-wrap justify-center gap-3">
            {member.specialties.map((spec, idx) => (
              <div
                key={idx}
                className="bg-white px-5 py-3 rounded-xl border border-champagne/50 shadow-sm text-sm font-semibold text-[#2E6B4C] flex items-center space-x-2"
              >
                <span className="w-2 h-2 rounded-full bg-[#2E6B4C]" />
                <span>{spec}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA Footer Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 md:p-12 text-white shadow-2xl border border-white/10 text-center space-y-6">
          <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight text-amber-200">
            {t.ctaHeading}
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-sm md:text-base">
            {t.ctaSub}
          </p>
          <div className="pt-2">
            <Link
              to="/book-form"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#2E6B4C] text-white font-bold rounded-xl hover:bg-[#23533a] transition-all shadow-lg hover:shadow-emerald-900/30 text-base"
            >
              <span>{t.ctaButton}</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamMemberDetailPage;
